/**
 * Seções centrais da Tela 2 (T2-1): Identidade · Identidades · Bonds.
 * Todas consomem o contrato REAL de `GET /v1/persons/:id` (sem stubs).
 */

import { Send } from 'lucide-react';
import { useState } from 'react';

import { useCapability } from '../../hooks/use-capability';
import { BondManagementDrawer } from '../bonds/BondManagementDrawer';

import { BondAutonomyEditor } from './BondAutonomyEditor';
import { BondEngagementEditor } from './BondEngagementEditor';
import {
  bondLabel,
  guardianshipStatusLabel,
  maskCpf,
  bondRolesLabel,
  bondStatusLabel,
  bondIsAdversarial,
  nativeSchedulingModeLabel,
  aiEngagementModeLabel,
  channelLabel,
  deriveGuardianship,
  legalCapacityLabel,
  personInitials,
  formatShortDate,
} from './persons-adapter';
import { PersonSection } from './PersonSection';
import {
  GuardianshipIndicator,
  LegalCapacityIndicator,
  PersonBondBadge,
  RiskBadge,
} from './pessoas-badges';

import type { PersonDetail } from './types';

// ─── 1. Identidade (header card) ────────────────────────────────────────

export function IdentitySection({
  person,
  onStartConversation,
}: {
  person: PersonDetail;
  onStartConversation: () => void;
}): JSX.Element {
  const guard = deriveGuardianship(person.guardianshipsAsWard);
  const cpf =
    person.primaryIdentifierType === 'cpf' && person.primaryIdentifierValue !== null
      ? `CPF ${maskCpf(person.primaryIdentifierValue)}`
      : (person.primaryIdentifierValue ?? 'Sem identificador primário');

  return (
    <PersonSection id="identity" label="Identidade">
      <div className="person-identity">
        <div className="person-identity__avatar">{personInitials(person.displayName)}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="person-identity__name">{person.displayName}</div>
          <div className="person-identity__sub">
            {cpf} · cliente desde {formatShortDate(person.createdAt)}
            {person.updatedAt !== null && <> · atualizado {formatShortDate(person.updatedAt)}</>}
          </div>
          <div className="person-identity__attrs">
            <RiskBadge score={person.riskScore} />
            {guard.state !== 'none' && (
              <GuardianshipIndicator state={guard.state} label={guard.label} />
            )}
            {person.legalCapacity !== null && (
              <LegalCapacityIndicator label={legalCapacityLabel(person.legalCapacity)} />
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <button
            className="btn btn--secondary btn--sm"
            type="button"
            onClick={onStartConversation}
          >
            <Send size={12} />
            Iniciar conversa
          </button>
        </div>
      </div>

      {/* Detalhe da guardianship — só campos REAIS (sem nome fabricado do responsável). */}
      {guard.state !== 'none' && guard.source !== null && (
        <div className="regulated-alert" style={{ marginTop: 'var(--space-2)' }}>
          <div style={{ flex: 1 }}>
            <strong>{guard.label}</strong>
            <br />
            Status: {guardianshipStatusLabel(guard.source.status)}
            {guard.source.effectiveUntil !== null && (
              <>
                {' '}
                · válida até{' '}
                <span className="mono">{formatShortDate(guard.source.effectiveUntil)}</span>
              </>
            )}
          </div>
        </div>
      )}
    </PersonSection>
  );
}

// ─── 2. Identidades (multi-canal) ───────────────────────────────────────

export function IdentidadesSection({ person }: { person: PersonDetail }): JSX.Element {
  return (
    <PersonSection id="identidades" label="Identidades" count={person.identities.length}>
      {person.identities.length === 0 ? (
        <div className="person-empty">Nenhuma identidade de canal registrada.</div>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {person.identities.map((id) => (
            <li key={id.id} className="person-identity-row">
              <span className="person-identity-row__channel">{channelLabel(id.channel)}</span>
              <span className="person-identity-row__value">{id.channelIdentifier}</span>
              <span className="person-identity-row__flags">
                <span
                  className={
                    'person-identity-row__flag' +
                    (id.verified ? ' person-identity-row__flag--ok' : '')
                  }
                >
                  {id.verified ? 'verificado' : 'não verificado'}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </PersonSection>
  );
}

// ─── 2-bis. Dados coletados (Coleta → Person.metadata.collected) ──────────

const COLLECTED_LABELS: Record<string, string> = {
  logradouro: 'Logradouro',
  numero: 'Número',
  complemento: 'Complemento',
  bairro: 'Bairro',
  cidade: 'Cidade',
  uf: 'UF',
  cep: 'CEP',
  birthDate: 'Data de nascimento',
  rg: 'RG',
  email: 'E-mail',
  phone: 'Telefone',
};

function humanizeCollectedKey(key: string): string {
  return COLLECTED_LABELS[key] ?? key.charAt(0).toUpperCase() + key.slice(1);
}

/**
 * Dados coletados na conversa pela feature Coleta (structured-collection) que o
 * `finalize` funde em `Person.metadata.collected`. Marcados **não verificados**
 * (são autodeclarados até confirmação — alinha com o modelo de identidade do
 * Item 391). Some quando não há nada coletado.
 */
export function DadosColetadosSection({ person }: { person: PersonDetail }): JSX.Element | null {
  const meta = person.metadata as Record<string, unknown> | null;
  const rawCollected = meta !== null && typeof meta === 'object' ? meta.collected : null;
  const collected =
    rawCollected !== null && typeof rawCollected === 'object'
      ? (rawCollected as Record<string, unknown>)
      : null;
  const entries =
    collected !== null
      ? Object.entries(collected).filter(([, v]) => v !== null && v !== undefined && v !== '')
      : [];
  if (entries.length === 0) return null;
  return (
    <PersonSection id="coletados" label="Dados coletados" count={entries.length}>
      <div className="person-empty" style={{ marginBottom: 'var(--space-2)' }}>
        Coletados em conversa (via Coleta) — ainda <strong>não verificados</strong>.
      </div>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {entries.map(([key, value]) => (
          <li key={key} className="person-identity-row">
            <span className="person-identity-row__channel">{humanizeCollectedKey(key)}</span>
            <span className="person-identity-row__value">{String(value)}</span>
          </li>
        ))}
      </ul>
    </PersonSection>
  );
}

// ─── 3. Bonds ───────────────────────────────────────────────────────────

export function BondsSection({ person }: { person: PersonDetail }): JSX.Element {
  // Gestão pessoa-cêntrica dos sub-recursos do Bond (Item 351). O vínculo é
  // durável (vive além das conversas); aqui se gerencia direto da ficha.
  const canManageBond = useCapability('bond-basic');
  // Frente B Plano 2 peça 4 — editar a autonomia exige a mesma cap de gestão do vínculo.
  // Frente B Sessão 3 — autonomia de agenda por-cliente só significa algo p/ tenant
  // gestor de agenda nativa; fora dele a marca não é exibida (inerte).
  const hasNativeScheduling = useCapability('scheduling-native');
  // S4 PR-b (Corrente de Compromissos, Item 404 faceta b) — a política de
  // atuação da IA por-cliente só é significativa/editável sob a cap opt-in.
  const hasEngagementPolicy = useCapability('client-ai-engagement-policy');
  const [managing, setManaging] = useState<{ bondId: string; label: string } | null>(null);

  return (
    <PersonSection id="bonds" label="Bonds" count={person.bonds.length}>
      {person.bonds.length === 0 ? (
        <div className="person-empty">Nenhum bond vinculado a esta pessoa.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {person.bonds.map((b) => {
            const label = bondLabel(b);
            // Sessão 3 — autonomia exibida só p/ tenant nativo, no bond onde a pessoa
            // é participant e tem override (≠ herdar). Sinal de "este cliente é especial".
            const autonomyLabel = hasNativeScheduling
              ? nativeSchedulingModeLabel(b.nativeSchedulingMode)
              : null;
            const showWaitlistPref = hasNativeScheduling && b.waitlistPreferred === true;
            // S4 PR-b — política de atuação da IA exibida só sob a cap opt-in,
            // no bond onde a pessoa é participant (molde autonomyLabel).
            const engagementLabel = hasEngagementPolicy
              ? aiEngagementModeLabel(b.aiEngagementMode)
              : null;
            return (
              <div key={b.id} className="person-bond-row">
                <PersonBondBadge type={b.type} label={label} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
                  <span className="person-bond-row__title">{label}</span>
                  <span className="person-bond-row__role">
                    Papel: {bondRolesLabel(b.personRoles)} · estado{' '}
                    <strong>{bondStatusLabel(b.status)}</strong>
                    {bondIsAdversarial(b) && (
                      <span style={{ color: '#6B46C1', marginLeft: 8 }}>· adversarial</span>
                    )}
                  </span>
                  {(autonomyLabel !== null || showWaitlistPref) && (
                    <span
                      className="person-bond-row__role"
                      data-testid={`bond-autonomy-${b.id}`}
                      style={{ color: 'var(--accent-fg)' }}
                    >
                      Agenda: {autonomyLabel ?? 'herda padrão'}
                      {showWaitlistPref && ' · prioriza fila'}
                    </span>
                  )}
                  {/* Peça 4 — editar a autonomia por-cliente (só p/ tenant nativo, quem gere o
                      vínculo, e onde a pessoa é participant → tem participantId/version). */}
                  {hasNativeScheduling &&
                    canManageBond &&
                    b.personRoles.includes('participant') &&
                    typeof b.participantId === 'string' &&
                    typeof b.participantVersion === 'number' && (
                      <BondAutonomyEditor
                        personId={person.id}
                        bondId={b.id}
                        participantId={b.participantId}
                        version={b.participantVersion}
                        currentMode={b.nativeSchedulingMode ?? null}
                        currentPreferred={b.waitlistPreferred ?? null}
                      />
                    )}
                  {engagementLabel !== null && (
                    <span
                      className="person-bond-row__role"
                      data-testid={`bond-engagement-${b.id}`}
                      style={{ color: 'var(--accent-fg)' }}
                    >
                      IA: {engagementLabel}
                    </span>
                  )}
                  {/* S4 PR-b — editar a política de atuação da IA por-cliente (cap opt-in,
                      quem gere o vínculo, e onde a pessoa é participant). */}
                  {hasEngagementPolicy &&
                    canManageBond &&
                    b.personRoles.includes('participant') &&
                    typeof b.participantId === 'string' &&
                    typeof b.participantVersion === 'number' && (
                      <BondEngagementEditor
                        personId={person.id}
                        bondId={b.id}
                        participantId={b.participantId}
                        version={b.participantVersion}
                        currentMode={b.aiEngagementMode ?? null}
                      />
                    )}
                </div>
                <span className="person-bond-row__meta">desde {formatShortDate(b.createdAt)}</span>
                {canManageBond && (
                  <button
                    type="button"
                    className="person-bond-row__manage"
                    onClick={() => setManaging({ bondId: b.id, label })}
                  >
                    Gerenciar vínculo
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <BondManagementDrawer
        bondId={managing?.bondId ?? null}
        bondLabel={managing?.label ?? ''}
        onClose={() => setManaging(null)}
      />
    </PersonSection>
  );
}
